import React, { useState, useEffect } from "react";
import { useLocation, useParams, useNavigate, Link } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import InnerBanner from "../components/PagesComponents/InnerBanner";
import ProductSlider from "../components/Common/ProductSlider";
import { showToast } from "../utils/toast";
import { postApi } from "../utils/api/api";
import DashboardSidebar from "../components/DashboardComponents/DashboardSidebar";

const BuyerProductEnquireDetail = () => {
  const baseUrl = "https://backend.tokoyarn.com/public/";
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { language } = useLanguage();
  const product = location.state?.product || null;
  console.log("Buyer product::", product);
  const [formData, setFormData] = useState({
    qty: "",
    yarn_count: "",
    currency: "",
    shippment: "",
    address: ""
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("product-details");

  useEffect(() => {
    if (!product) {
      showToast("error", "Product not found!");
    }
  }, [product]);

  // Validation function
  const validateField = (name, value) => {
    let error = "";
    
    switch (name) {
      case "qty":
        if (!value) error = "Quantity is required";
        else if (isNaN(value) || value <= 0) error = "Please enter a valid quantity";
        break;
      case "yarn_count":
        if (!value) error = "Yarn count is required";
        else if (isNaN(value) || value <= 0) error = "Please enter a valid yarn count";
        break;
      case "currency":
        if (!value) error = "Currency selection is required";
        break;
      case "shippment":
        if (!value) error = "Please specify if shipment is included";
        break;
      case "address":
        if (!value) error = "Address is required";
        else if (value.length > 500) error = "Address cannot exceed 500 characters";
        break;
      default:
        break;
    }
    
    return error;
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Validate field when it's being changed and has been touched
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  // Handle blur events to mark fields as touched
  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));

    // Validate the field
    const error = validateField(name, formData[name]);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleEnquireSubmit = async (e) => {
    e.preventDefault();
    
    // Mark all fields as touched to show validation errors
    const allTouched = {};
    Object.keys(formData).forEach(key => {
      allTouched[key] = true;
    });
    setTouched(allTouched);
    
    // Validate all fields
    const newErrors = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });
    
    setErrors(newErrors);
    
    // Check if there are any errors
    if (Object.keys(newErrors).length > 0) {
      showToast("error", "Please fix the form errors before submitting.");
      return;
    }

    setLoading(true);
    try {
      const response = await postApi(`buyer/enquiry-product/${id}`, formData);

      if (response.success) {
        showToast("success", "Your enquiry has been submitted successfully!");
        navigate(`/buyer-dashboard/enquire`);
      } else {
        showToast("error", "Failed to submit enquiry. Please try again.");
      }
    } catch (error) {
      showToast("error", error.message || "An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatKey = (key) => {
    if (!key) return null;
    return key
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const renderQualityBadge = (grade) => {
    const colors = {
      'A': 'bg-success',
      'B': 'bg-primary',
      'C': 'bg-warning',
      'D': 'bg-danger'
    };
    return (
      <span className={`badge rounded-pill text-white fw-bold px-3 py-2 ${colors[grade] || 'bg-secondary'}`}>
        Grade {grade}
      </span>
    );
  };

  const renderBlendInfo = (spun) => {
    if (!spun || !spun.blend1) return null;
    
    return (
      <div className="blend-info mt-4 pt-3 border-top">
        <h5 className="text-primary mb-3"><i className="bi bi-bezier2 me-2"></i>Blend Composition</h5>
        <div className="mb-2" style={{ height: '30px', borderRadius: '8px' }}>
          <div className="row">
            <div className="col-md-6">
              <div className="p-3 bg-white rounded-3 border position-relative" style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                <div className="position-absolute top-0 start-0 bg-primary" style={{ width: '4px', height: '100%', borderTopLeftRadius: '8px', borderBottomLeftRadius: '8px' }}></div>
                <small className="text-muted d-block">Blend 1</small>
                <div className="fw-bold fs-6 mt-1 text-dark">{spun.blend1} - ({spun.blend1_percentage}%)</div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="p-3 bg-white rounded-3 border position-relative" style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                <div className="position-absolute top-0 start-0 bg-info" style={{ width: '4px', height: '100%', borderTopLeftRadius: '8px', borderBottomLeftRadius: '8px' }}></div>
                <small className="text-muted d-block">Blend 2</small>
                <div className="fw-bold fs-6 mt-1 text-dark">{spun.blend2} - ({spun.blend2_percentage}%)</div>
              </div>
            </div>
          </div>            
        </div>
      </div>
    );
  };

  if (!product) return <div className="container py-5"><div className="alert alert-danger">Product not found!</div></div>;

  return (
    <>
      <section className="product-detail py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              {/* Product Card with Floating Elements */}
              <div className="card shadow-lg border-0 overflow-hidden mb-4" style={{ borderRadius: '20px', position: 'relative' }}>
                {/* Decorative Elements */}
                <div className="position-absolute top-0 end-0 bg-primary" style={{ width: '80px', height: '80px', clipPath: 'polygon(0 0, 100% 0, 100% 100%)' }}></div>
                
                <div className="card-body p-lg-5">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <h1 className="text-primary fw-bold mb-1" style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.1)' }}>{product.type} Yarn</h1>
                      <p className="text-muted fs-5 mb-0">
                        {product.spun?.category} - {product.spun?.sub_category}
                      </p>
                    </div>
                    {renderQualityBadge(product.quality_grade)}
                  </div>
                  
                  <div className="row g-5">
                    {/* Left Column - Image Slider */}
                    <div className="col-lg-6 d-flex align-items-center justify-content-center">
                      <div className="bg-white rounded-3 p-3 shadow-sm border position-relative" style={{ width: '100%', maxWidth: '500px' }}>
                        <div className="position-absolute top-0 start-0 m-3">
                          <span className="badge bg-danger text-white px-3 py-2 fw-bold">HOT</span>
                        </div>
                        {product.images && JSON.parse(product.images).length ? (
                           <ProductSlider images={product.images} />
                        ) : (
                          <div className="d-flex flex-column align-items-center justify-content-center text-secondary py-5" style={{ minHeight: '300px' }}>
                            <i className="bi bi-image-fill fs-1"></i>
                            <span className="ms-2 mt-2">No images available</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right Column - Details */}
                    <div className="col-lg-6">
                      <div className="price-display my-4 p-3 bg-gradient-light rounded-4 shadow-sm">
                        <div className="d-flex flex-wrap justify-content-between align-items-center">
                          <span className="text-muted fw-bold">Current Price:</span>
                          <h4 className="display-7 price--tag-in text-success fw-bold">
                            {product.spun?.price} <span className="price--type">{product.price_type} </span>
                          </h4>
                        </div>
                      </div>

                      <div className="specs-grid mb-4">
                        <h5 className="mb-3 text-dark d-flex align-items-center">
                          <span className="bg-primary text-white p-2 rounded-circle me-3 d-flex align-items-center justify-content-center" style={{ width: '36px', height: '36px' }}>
                            <i className="bi bi-list-check"></i>
                          </span>
                          Key Specifications
                        </h5>
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="p-3 bg-white rounded-3 border position-relative " style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                              <div className="position-absolute top-0 start-0 bg-primary" style={{ width: '4px', height: '100%', borderTopLeftRadius: '8px', borderBottomLeftRadius: '8px' }}></div>
                              <small className="text-muted d-block">End Use</small>
                              <div className="fw-bold fs-6 mt-1 text-dark">{product.spun?.end_use || 'N/A'}</div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="p-3 bg-white rounded-3 border position-relative " style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                              <div className="position-absolute top-0 start-0 bg-info" style={{ width: '4px', height: '100%', borderTopLeftRadius: '8px', borderBottomLeftRadius: '8px' }}></div>
                              <small className="text-muted d-block">Technology</small>
                              <div className="fw-bold fs-6 mt-1 text-dark">{product.spun?.technology || 'N/A'}</div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="p-3 bg-white rounded-3 border position-relative" style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                              <div className="position-absolute top-0 start-0 bg-warning" style={{ width: '4px', height: '100%', borderTopLeftRadius: '8px', borderBottomLeftRadius: '8px' }}></div>
                              <small className="text-muted d-block">Ply</small>
                              <div className="fw-bold fs-6 mt-1 text-dark">
                                {product.spun?.ply}
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="p-3 bg-white rounded-3 border position-relative" style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                              <div className="position-absolute top-0 start-0 bg-success" style={{ width: '4px', height: '100%', borderTopLeftRadius: '8px', borderBottomLeftRadius: '8px' }}></div>
                              <small className="text-muted d-block">Application</small>
                              <div className="fw-bold fs-6 mt-1 text-dark">
                                {product.spun?.application}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      {renderBlendInfo(product.spun)}

                      
                    </div>
                    <div className="col-lg-12">
                      {/* Enquire Form */}
                      <div className="enquire-form mt-4 pt-3 border-top common-form-container">
                        <h5 className="text-primary mb-3 d-flex align-items-center">
                          <i className="bi bi-question-circle me-2"></i>Make an Enquiry
                        </h5>
                        <form onSubmit={handleEnquireSubmit} className="bg-light p-4 rounded-3">
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <label htmlFor="qty" className="form-label fw-bold">
                                Quantity <span className="text-danger">*</span>
                              </label>
                              <input
                                type="number"
                                id="qty"
                                name="qty"
                                className={`form-control ${errors.qty && touched.qty ? 'is-invalid' : ''}`}
                                placeholder="Enter quantity"
                                value={formData.qty}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                              />
                              {errors.qty && touched.qty && (
                                <div className="invalid-feedback d-block">{errors.qty}</div>
                              )}
                            </div>
                            <div className="col-md-6 mb-3">
                              <label htmlFor="yarn_count" className="form-label fw-bold">
                                Yarn Count <span className="text-danger">*</span>
                              </label>
                              <input
                                type="number"
                                id="yarn_count"
                                name="yarn_count"
                                className={`form-control ${errors.yarn_count && touched.yarn_count ? 'is-invalid' : ''}`}
                                placeholder="Enter yarn count"
                                value={formData.yarn_count}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                              />
                              {errors.yarn_count && touched.yarn_count && (
                                <div className="invalid-feedback d-block">{errors.yarn_count}</div>
                              )}
                            </div>
                            <div className="col-md-6 mb-3">
                              <label htmlFor="currency" className="form-label fw-bold">
                                Quotation Currency <span className="text-danger">*</span>
                              </label>
                              <select
                                id="currency"
                                name="currency"
                                className={`form-select form-control ${errors.currency && touched.currency ? 'is-invalid' : ''}`}
                                value={formData.currency}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                              >
                                <option value="">Select Currency</option>
                                <option value="Dollar">Dollar</option>
                                <option value="Rupiah">Rupiah</option>
                              </select>
                              {errors.currency && touched.currency && (
                                <div className="invalid-feedback d-block">{errors.currency}</div>
                              )}
                            </div>
                            <div className="col-md-6 mb-3">
                              <label htmlFor="shippment" className="form-label fw-bold">
                                Include Shipment <span className="text-danger">*</span>
                              </label>
                              <select
                                id="shippment"
                                name="shippment"
                                className={`form-select form-control ${errors.shippment && touched.shippment ? 'is-invalid' : ''}`}
                                value={formData.shippment}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                              >
                                <option value="">Select Option</option>
                                <option value="Yes">Yes</option>
                                <option value="No">No</option>
                              </select>
                              {errors.shippment && touched.shippment && (
                                <div className="invalid-feedback d-block">{errors.shippment}</div>
                              )}
                            </div>
                            <div className="col-12 mb-3">
                              <label htmlFor="address" className="form-label fw-bold">
                                Address <span className="text-danger">*</span>
                              </label>
                              <textarea
                                id="address"
                                name="address"
                                className={`form-control ${errors.address && touched.address ? 'is-invalid' : ''}`}
                                placeholder="Enter your address"
                                rows="3"
                                maxLength="500"
                                value={formData.address}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                              />
                              <div className="d-flex justify-content-between mt-1">
                                <small className="text-muted">{formData.address.length}/500 characters</small>
                                {errors.address && touched.address && (
                                  <small className="text-danger">{errors.address}</small>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="d-flex justify-content-center">
                          <button 
                            type="submit" 
                            className="submit-button" 
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Submitting...
                              </>
                            ) : (
                              'Submit Enquiry'
                            )}
                          </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabs Navigation */}
              <ul className="nav nav-pills mt-5 justify-content-center" id="productTabs" role="tablist">
                <li className="nav-item mx-2" role="presentation">
                  <button
                    className={`nav-link rounded-pill px-4 py-2 fw-bold ${activeTab === "product-details" ? "active bg-primary text-white shadow-sm" : "bg-white text-dark"}`}
                    id="product-details-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#product-details"
                    type="button"
                    role="tab"
                    aria-controls="product-details"
                    aria-selected={activeTab === "product-details"}
                    onClick={() => setActiveTab("product-details")}
                  >
                    <i className="bi bi-box-seam me-2"></i>Product Specifications
                  </button>
                </li>
              </ul>

              {/* Tabs Content */}
              <div className="tab-content mt-4" id="productTabsContent">
                <div
                  className={`tab-pane fade ${activeTab === "product-details" ? "show active" : ""}`}
                  id="product-details"
                  role="tabpanel"
                  aria-labelledby="product-details-tab"
                >
                  <div className="card shadow-sm border-0 overflow-hidden" style={{ borderRadius: '15px' }}>
                    <div className="card-body p-4 p-lg-5">
                      <div className="row g-4">
                        <div className="col-md-6">
                          <div className="card border-0 shadow-sm p-3" style={{ borderRadius: '12px' }}>
                            <div className="card-header bg-transparent border-0 d-flex align-items-center">
                              <div className="bg-primary text-white p-2 rounded-circle me-3 d-flex align-items-center justify-content-center" style={{ width: '36px', height: '36px' }}>
                                <i className="bi bi-info-circle"></i>
                              </div>
                              <h4 className="mb-0 text-primary fw-bold">Basic Information</h4>
                            </div>
                            <div className="card-body">
                              <div className="list-group list-group-flush">
                                {[
                                  { label: "Product Type", value: product.type },
                                  { label: "Category", value: product.spun?.category },
                                  { label: "Sub Category", value: product.spun?.sub_category },
                                  { label: "Quality Grade", value: product.quality_grade },
                                  { label: "IPI", value: product.ipi }
                                ].map((item, index) => (
                                  <div key={index} className="list-group-item d-flex justify-content-between align-items-center px-0 py-3 border-bottom">
                                    <span className="fw-bold text-muted">{item.label}</span>
                                    <span className="text-dark">{item.value || 'N/A'}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="col-md-6">
                          <div className="card border-0 shadow-sm p-3" style={{ borderRadius: '12px' }}>
                            <div className="card-header bg-transparent border-0 d-flex align-items-center">
                              <div className="bg-primary text-white p-2 rounded-circle me-3 d-flex align-items-center justify-content-center" style={{ width: '36px', height: '36px' }}>
                                <i className="bi bi-gear-fill"></i>
                              </div>
                              <h4 className="mb-0 text-primary fw-bold">Technical Specifications</h4>
                            </div>
                            <div className="card-body">
                              <div className="list-group list-group-flush">
                                {[
                                  { label: "Count", value: `${product.spun?.count_from} - ${product.spun?.count_to}` },
                                  { label: "Avg CSP", value: `${product.spun?.avg_csp_from} - ${product.spun?.avg_csp_to}` },
                                  { label: "Yarn Speciality", value: product.spun?.yarn_speciality },
                                  { label: "Price Type", value: product.price_type }
                                ].map((item, index) => (
                                  <div key={index} className="list-group-item d-flex justify-content-between align-items-center px-0 py-3 border-bottom">
                                    <span className="fw-bold text-muted">{item.label}</span>
                                    <span className="text-dark">{item.value || 'N/A'}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Certificate Section */}
                      {product.certificate && (
                        <div className="mt-4 pt-4 border-top">
                          <div className="d-flex align-items-center mb-3">
                            <div className="bg-primary text-white p-2 rounded-circle me-3 d-flex align-items-center justify-content-center" style={{ width: '36px', height: '36px' }}>
                              <i className="bi bi-file-earmark-text-fill"></i>
                            </div>
                            <h5 className="mb-0 text-primary fw-bold">Certificates</h5>
                          </div>
                          <div className="d-flex align-items-center">
                            <i className="bi bi-file-earmark-pdf-fill text-danger fs-3 me-3"></i>
                            <div>
                              <p className="mb-1 fw-bold">Quality Certificate</p>
                              <small className="text-muted">PDF Document</small>
                            </div>
                            <a 
                              href={`${baseUrl}${product.certificate}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="btn btn-outline-primary rounded-pill px-4 ms-auto"
                            >
                              <i className="bi bi-download me-2"></i>Download
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default BuyerProductEnquireDetail;